import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DisplayProvider } from "@/context/DisplayContext";
import LoginPage from "@/pages/LoginPage";
import Sidebar from "@/components/Sidebar";
import DisplayToolbar from "@/components/DisplayToolbar";
import DashboardPage from "@/pages/DashboardPage";
import DebtTrackerPage from "@/pages/DebtTrackerPage";
import MonthlyBudgetPage from "@/pages/MonthlyBudgetPage";
import PayoffPlanPage from "@/pages/PayoffPlanPage";
import PaymentViewPage from "@/pages/PaymentViewPage";
import StrategyPage from "@/pages/StrategyPage";
import SettingsPage from "@/pages/SettingsPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DayTradingPage from "@/pages/DayTradingPage";
import AppSelector from "@/components/AppSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios globally for credentials
axios.defaults.withCredentials = true;

// ── Global error guards ───────────────────────────────────────
// Catches errors that escape React's render tree (async listeners,
// timers, third-party scripts) so they're logged silently instead of
// surfacing as uncaught exceptions in production.
if (typeof window !== "undefined" && !window.__vault_error_guards_installed__) {
  window.__vault_error_guards_installed__ = true;
  window.addEventListener("error", (e) => {
    try {
      // eslint-disable-next-line no-console
      console.warn("[window.onerror]", e?.message || e?.error?.message || e, e?.filename, e?.lineno);
    } catch { /* noop */ }
    // Do not preventDefault — let dev tools see it, but no crash dialog.
  });
  window.addEventListener("unhandledrejection", (e) => {
    try {
      // eslint-disable-next-line no-console
      console.warn("[unhandledrejection]", e?.reason?.message || e?.reason || e);
    } catch { /* noop */ }
    // Prevent the browser's default "uncaught (in promise)" red error page logic.
    if (typeof e?.preventDefault === "function") {
      try { e.preventDefault(); } catch { /* noop */ }
    }
  });
}

function AppContent() {
  const { user, loading: authLoading, logout } = useAuth();
  // Lazy-init activeView from the user's stored workspace preference so
  // returning users land directly in their chosen app and skip the
  // selector. New users (no preference) default to "dashboard" and the
  // selector overlays via `showSelector` below.
  const [activeView, setActiveView] = useState(() => {
    try {
      const pref = localStorage.getItem("vault_preferred_app");
      if (pref === "trading") return "day-trading";
    } catch { /* SSR / private mode */ }
    return "dashboard";
  });
  // Show the post-login app selector ONLY when no preference is stored.
  // A user can hit the selector again via the "Switch app" link in the
  // sidebar (which clears the key + reloads).
  const [showSelector, setShowSelector] = useState(() => {
    try {
      // If a deep-link URL like /day-trading was used, never block it
      // with the selector — the user explicitly chose a workspace.
      const p = typeof window !== "undefined" ? window.location.pathname : "/";
      if (p === "/day-trading" || p.startsWith("/day-trading/")) return false;
      return !localStorage.getItem("vault_preferred_app");
    } catch {
      return false;
    }
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [debts, setDebts] = useState([]);
  const [budget, setBudget] = useState([]);
  const [payoffPlan, setPayoffPlan] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDebts = useCallback(async () => {
    try { const res = await axios.get(`${API}/debts`); setDebts(res.data); } catch (e) { console.error(e); }
  }, []);
  const fetchBudget = useCallback(async () => {
    try { const res = await axios.get(`${API}/budget`); setBudget(res.data); } catch (e) { console.error(e); }
  }, []);
  const fetchPayoffPlan = useCallback(async () => {
    try { const res = await axios.get(`${API}/payoff-plan`); setPayoffPlan(res.data); } catch (e) { console.error(e); }
  }, []);
  const fetchDashboard = useCallback(async () => {
    try { const res = await axios.get(`${API}/dashboard`); setDashboard(res.data); } catch (e) { console.error(e); }
  }, []);
  const fetchMonths = useCallback(async () => {
    try { const res = await axios.get(`${API}/months`); setMonths(res.data); } catch (e) { console.error(e); }
  }, []);

  const seedData = useCallback(async () => {
    try { await axios.post(`${API}/seed`); } catch (e) { console.error(e); }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchDebts(), fetchBudget(), fetchPayoffPlan(), fetchDashboard(), fetchMonths()]);
    setLoading(false);
  }, [fetchDebts, fetchBudget, fetchPayoffPlan, fetchDashboard, fetchMonths]);

  useEffect(() => {
    if (user) {
      const init = async () => { await seedData(); await refreshAll(); };
      init();
    }
  }, [user, seedData, refreshAll]);

  // ── URL sync for Day Trading mode ─────────────────────────
  // Day Trading lives at `/day-trading[/<tab-slug>]` and is owned by
  // React Router (DayTradingPage uses `useNavigate` + `useParams`).
  // AppContent only needs to flip its own view-state when the URL
  // matches a day-trading path, and to push "/" when leaving day-
  // trading via the "Back to The Vault" button.
  useEffect(() => {
    const p = window.location.pathname;
    if (p === "/day-trading" || p.startsWith("/day-trading/")) setActiveView("day-trading");
    const onPop = () => {
      const pp = window.location.pathname;
      setActiveView(pp === "/day-trading" || pp.startsWith("/day-trading/") ? "day-trading" : "dashboard");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Push "/" only when the user explicitly leaves day-trading (back button).
  // No URL push when entering — the user enters via a state-change that is
  // already tied to a URL navigation in either direction.
  const prevViewRef = useRef(activeView);
  useEffect(() => {
    const wasDayTrading = prevViewRef.current === "day-trading";
    const isDayTrading  = activeView === "day-trading";
    prevViewRef.current = activeView;
    if (wasDayTrading && !isDayTrading) {
      const path = window.location.pathname;
      if (path === "/day-trading" || path.startsWith("/day-trading/")) {
        window.history.pushState({ view: activeView }, "", "/");
      }
    } else if (!wasDayTrading && isDayTrading) {
      const path = window.location.pathname;
      if (path !== "/day-trading" && !path.startsWith("/day-trading/")) {
        window.history.pushState({ view: "day-trading" }, "", "/day-trading");
      }
    }
  }, [activeView]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading...</p>
      </div>
    );
  }

  // Not authenticated - show login
  if (!user) {
    return <LoginPage />;
  }

  // Post-login workspace selector: appears for any user who hasn't yet
  // picked a remembered workspace. Selecting a card flips activeView
  // and dismisses the selector for the current session.
  if (showSelector) {
    return (
      <AppSelector
        onSelectVault={() => { setActiveView("dashboard");   setShowSelector(false); }}
        onSelectTrading={() => { setActiveView("day-trading"); setShowSelector(false); }}
        onLogout={logout}
      />
    );
  }

  const renderView = () => {
    switch (activeView) {
      case "dashboard":
        return <DashboardPage dashboard={dashboard} debts={debts} budget={budget} loading={loading} api={API} />;
      case "debt-tracker":
        return <DebtTrackerPage debts={debts} onRefresh={refreshAll} api={API} />;
      case "monthly-budget":
        return <MonthlyBudgetPage debts={debts} onRefresh={refreshAll} api={API} />;
      case "payoff-plan":
        return <PayoffPlanPage payoffPlan={payoffPlan} debts={debts} months={months} onRefresh={refreshAll} api={API} dashboard={dashboard} />;
      case "payment-view":
        return <PaymentViewPage debts={debts} months={months} api={API} />;
      case "strategy":
        return <StrategyPage debts={debts} />;
      case "settings":
        return <SettingsPage user={user} api={API} onRefresh={refreshAll} />;
      default:
        return <DashboardPage dashboard={dashboard} debts={debts} budget={budget} loading={loading} api={API} />;
    }
  };

  // Day Trading runs as a standalone full-screen app (no sidebar / no Vault chrome).
  // Renders before the budget shell so users feel like they've switched into a
  // dedicated trading terminal.
  if (activeView === "day-trading") {
    return (
      <>
        <DayTradingPage onBackToVault={() => {
          // "Back to The Vault" returns to the workspace selector (not
          // straight to the budget dashboard) so users can pick again.
          // Clears the remembered preference so a refresh also lands on
          // the selector — mirrors the sidebar's "Switch app" behaviour.
          try { localStorage.removeItem("vault_preferred_app"); } catch { /* noop */ }
          setShowSelector(true);
          setActiveView("dashboard");
        }} />
        <Toaster position="top-right" />
      </>
    );
  }

  return (
    <div className="flex h-screen bg-white" data-testid="app-layout">
      <Sidebar activeView={activeView} onNavigate={setActiveView} user={user} onLogout={logout} collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-8 pt-14 md:pt-8 transition-all duration-200">
        {renderView()}
      </main>
      {activeView !== "monthly-budget" && <DisplayToolbar />}
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <DisplayProvider>
          <Routes>
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/day-trading" element={<AppContent />} />
            <Route path="/day-trading/:tab" element={<AppContent />} />
            <Route path="/day-trading/:tab/:sub" element={<AppContent />} />
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </DisplayProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
